import React, { useState } from 'react';
import { useOrgContext } from '@/contexts/OrgContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { EventCard } from '@/components/org/EventCard';
import { Button } from '@/components/ui/button';
import { Loader2, Calendar, Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { format, parseISO, isBefore, isAfter } from 'date-fns';
import { z } from 'zod';
import { createEventSchema } from '@tyto/shared';
import { useNavigate } from 'react-router';
import { BackButton } from '@/components/ui/BackButton';
import { PageTopNav } from '@/components/nav/PageTopNav';
import { Link } from 'react-router';

// Define the event type based on the database schema
interface Event {
  id: string;
  orgId: string;
  name: string;
  description: string;
  startDate: string;
  createdAt: string;
  updatedAt: string;
}

type EventFormValues = z.infer<typeof createEventSchema>;

const EventsOrgAdmin: React.FC = () => {
  const { orgRole, organisation } = useOrgContext()
  const navigate = useNavigate();
  const orgId = organisation?.id;

  // Fetch events from the organization
  const { data: events, isLoading, error } = useQuery({
    queryKey: ['organizationEvents'],
    queryFn: async () => {
      const response = await fetch(`/api/org/${orgId}/events`);
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      return response.json() as Promise<Event[]>;
    },
  });

  // Group events by past and upcoming
  const now = new Date();
  const upcomingEvents = events?.filter(event => isAfter(parseISO(event.startDate), now)) || [];
  const pastEvents = events?.filter(event => isBefore(parseISO(event.startDate), now)) || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading events...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-destructive p-4">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        <p>Error loading events. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto ">
      <PageTopNav pageName={organisation?.name ? `${organisation.name} Events` : 'Events'}
        rightItem={orgRole === 'ADMIN' ? (
          <Link to={`/app/org/${orgId}/events/add`}>
            <Button>
              <Plus className="h-4 w-4" />
              <span className="hidden md:block">Add Event</span>
            </Button>
          </Link>
        ) : null} />


      {/* Events Tabs */}
      <div className="mb-8">
        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="upcoming" className="flex items-center gap-2">
              <Calendar className="h-5 w-5" /> Upcoming Events
            </TabsTrigger>
            <TabsTrigger value="past" className="flex items-center gap-2">
              <Calendar className="h-5 w-5" /> Past Events
            </TabsTrigger>
          </TabsList>
          <TabsContent value="upcoming">
            <div>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-4">
                  {upcomingEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No upcoming events scheduled.
                </p>
              )}
            </div>
          </TabsContent>
          <TabsContent value="past">
            <div>
              {pastEvents.length > 0 ? (
                <div className="space-y-4">
                  {pastEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      bgMuted
                    />
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No past events.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EventsOrgAdmin;
